import React, { useEffect, useRef } from 'react';
import { compose } from 'recompose';
import styled, { cx, StylesProps } from '../../../lib/styles';
import {
  raiseCampaignBannerCtaClick,
  raiseCampaignBannerView,
} from '../../../lib/gtm';

import campaignBannerStyles from './CampaignBanner_styles';

/**
 * The Campaign Banner fields. Shared between the standalone
 * `campaign_banner_block` and the banner nested in the Pillar Articles block.
 */
export interface CampaignBannerFields {
  /** Small overline text shown above the title (e.g. SPONSORED). */
  kicker?: string;
  /** The banner heading. Required — the banner does not render without it. */
  title: string;
  /** Supporting line beneath the title. */
  subtitle?: string;
  /** Label for the call-to-action button. */
  ctaLabel?: string;
  /** Destination for the call-to-action button. */
  ctaUrl?: string;
  /**
   * Banner background colour (hex or rgba). Text colour is automatically
   * chosen for readable contrast against it.
   */
  backgroundColor?: string;
  /**
   * Editor show/hide toggle. Only sent by the Pillar Articles block, where the
   * banner is nested and optional — the section renders only when this is true.
   * Absent for the standalone `campaign_banner_block`, which always renders.
   */
  enabled?: boolean;
}

export interface CampaignBannerProps extends CampaignBannerFields {
  /** Any additional classnames to add to the container element. */
  className?: string;
  /**
   * Where the banner is rendered, sent with the CTA click event to GA4 via GTM.
   *
   * @default 'collection'
   */
  placement?: string;
}

/**
 * A promotional banner with a kicker, title, subtitle and CTA on a solid
 * background colour. Text (and the CTA) automatically contrast against the
 * chosen background for readability.
 */
const CampaignBanner = ({
  kicker,
  title,
  subtitle,
  ctaLabel,
  ctaUrl,
  className,
  placement = 'collection',
  styles,
}: CampaignBannerProps & StylesProps<typeof campaignBannerStyles>) => {
  const rootRef = useRef<HTMLDivElement>(null);
  // Guards against duplicate impressions from React re-renders/state refreshes:
  // one true impression per rendered instance, for its whole lifetime.
  const hasTrackedView = useRef(false);

  useEffect(() => {
    const node = rootRef.current;
    if (!title || !node || hasTrackedView.current) return;

    const track = () => {
      if (hasTrackedView.current) return;
      hasTrackedView.current = true;
      raiseCampaignBannerView({ title, placement });
    };

    // No IntersectionObserver (old browser / SSR-hydration edge): fall back to
    // a single mount-based impression rather than dropping it entirely.
    if (typeof IntersectionObserver === 'undefined') {
      track();
      return;
    }

    // Fire once the banner is genuinely on screen (>=50% visible), then stop.
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          track();
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [title, placement]);

  if (!title) return null;

  const showCta = Boolean(ctaLabel && ctaUrl);

  // Push the CTA click to the dataLayer; GTM fires the GA4 event from here.
  const handleCtaClick = () =>
    raiseCampaignBannerCtaClick({ title, ctaLabel, placement });

  return (
    <div ref={rootRef} className={cx('CampaignBanner', styles.wrapper, className)}>
      <div className={styles.banner}>
        <div className={styles.content}>
          {kicker && <span className={styles.kicker}>{kicker}</span>}
          <span className={styles.title}>{title}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>

        {showCta && (
          <a
            className={styles.cta}
            href={ctaUrl}
            target={'_blank'}
            rel={'noopener noreferrer'}
            role={'button'}
            onClick={handleCtaClick}
          >
            {ctaLabel}
          </a>
        )}
      </div>
    </div>
  );
};

export default compose(styled(campaignBannerStyles))(CampaignBanner);
